// ==============================================
// Read what an APK says about itself, in the browser.
//
// An APK is a zip; AndroidManifest.xml inside it is Android's binary XML
// (AXML). From the manifest we take package, versionCode, versionName, split,
// minSdk/targetSdk and the application's android:label. A label that is a
// resource reference is looked up in resources.arsc. The vendor comes from the
// signing certificate's subject (O=, else CN=): the v1 PKCS#7 in
// META-INF/*.RSA|DSA|EC when there is one, else the certificate the v2/v3 APK
// Signing Block carries. Both are the real certificate, never a guess.
//
// Deliberately small and dependency-free: find the central directory, pull
// only the entries needed, inflate with the browser's DecompressionStream.
// Only the tail, a handful of entries and the signing block are ever read, so
// a 300 MB file costs milliseconds.
// ==============================================

export interface ApkManifestInfo {
  packageName: string;
  versionCode: number;
  versionName: string | null;
  minSdk: number | null;
  targetSdk: number | null;
  /** True when this is a split APK (config or feature), not the base. */
  isSplit: boolean;
  splitName: string | null;
}

export interface ApkInfo extends ApkManifestInfo {
  /** The app's name as the launcher shows it; a guess from the package when it cannot be read. */
  label: string | null;
  labelGuessed: boolean;
  /** O= (else CN=) of the signing certificate; null when there is no readable certificate. */
  vendor: string | null;
  vendorSource: 'v1' | 'v2' | 'v3' | null;
}

interface ParsedManifest extends ApkManifestInfo {
  labelText: string | null;
  labelRef: number | null;
}

const SIG_EOCD = 0x06054b50;
const SIG_CEN = 0x02014b50;
const SIG_LOC = 0x04034b50;

async function readRange(file: Blob, start: number, end: number): Promise<DataView> {
  return new DataView(await file.slice(Math.max(0, start), Math.min(file.size, end)).arrayBuffer());
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate-raw');
  // Copy onto a plain ArrayBuffer so Blob accepts it whatever the source buffer type.
  const copy = new Uint8Array(data.byteLength);
  copy.set(data);
  const stream = new Blob([copy.buffer]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

// ---- zip ----
interface ZipEntry { name: string; method: number; compSize: number; localOffset: number }
interface ZipIndex { entries: ZipEntry[]; cenOffset: number }

async function zipIndex(file: Blob): Promise<ZipIndex> {
  // End-of-central-directory record is in the last 64 KB (22 bytes + comment).
  const tailStart = Math.max(0, file.size - 65_557);
  const tail = await readRange(file, tailStart, file.size);
  let eocd = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) {
    if (tail.getUint32(i, true) === SIG_EOCD) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error('Not a zip file (no end-of-central-directory record)');
  const cenSize = tail.getUint32(eocd + 12, true);
  const cenOffset = tail.getUint32(eocd + 16, true);
  const cen = await readRange(file, cenOffset, cenOffset + cenSize);
  const dec = new TextDecoder();
  const entries: ZipEntry[] = [];
  let p = 0;
  while (p + 46 <= cen.byteLength && cen.getUint32(p, true) === SIG_CEN) {
    const nameLen = cen.getUint16(p + 28, true);
    const extraLen = cen.getUint16(p + 30, true);
    const commentLen = cen.getUint16(p + 32, true);
    entries.push({
      name: dec.decode(new Uint8Array(cen.buffer, cen.byteOffset + p + 46, nameLen)),
      method: cen.getUint16(p + 10, true),
      compSize: cen.getUint32(p + 20, true),
      localOffset: cen.getUint32(p + 42, true),
    });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return { entries, cenOffset };
}

async function readEntry(file: Blob, e: ZipEntry): Promise<Uint8Array> {
  const loc = await readRange(file, e.localOffset, e.localOffset + 30);
  if (loc.getUint32(0, true) !== SIG_LOC) throw new Error('Corrupt zip entry header');
  const dataStart = e.localOffset + 30 + loc.getUint16(26, true) + loc.getUint16(28, true);
  const raw = new Uint8Array(await file.slice(dataStart, dataStart + e.compSize).arrayBuffer());
  if (e.method === 0) return raw;
  if (e.method === 8) return inflateRaw(raw);
  throw new Error(`Unsupported zip compression method ${e.method}`);
}

async function zipEntry(file: Blob, index: ZipIndex, wantedName: string): Promise<Uint8Array> {
  const e = index.entries.find((x) => x.name === wantedName);
  if (!e) throw new Error(`${wantedName} not found in the APK`);
  return readEntry(file, e);
}

// ---- string pools (shared by AXML and resources.arsc) ----
const RES_STRING_POOL_TYPE = 0x0001;
const UTF8_FLAG = 1 << 8;

function readStringPool(dv: DataView, off: number): string[] {
  const stringCount = dv.getUint32(off + 8, true);
  const flags = dv.getUint32(off + 16, true);
  const stringsStart = dv.getUint32(off + 20, true);
  const utf8 = (flags & UTF8_FLAG) !== 0;
  const out: string[] = [];
  const base = off + stringsStart;
  const dec = new TextDecoder();
  for (let i = 0; i < stringCount; i++) {
    const idx = dv.getUint32(off + 28 + i * 4, true);
    let q = base + idx;
    if (utf8) {
      // two lengths (chars, then bytes), each one or two bytes
      const charLen = dv.getUint8(q); q += (charLen & 0x80) ? 2 : 1;
      let byteLen = dv.getUint8(q);
      if (byteLen & 0x80) { byteLen = ((byteLen & 0x7f) << 8) | dv.getUint8(q + 1); q += 2; } else { q += 1; }
      out.push(dec.decode(new Uint8Array(dv.buffer, dv.byteOffset + q, byteLen)));
    } else {
      let len = dv.getUint16(q, true); q += 2;
      if (len & 0x8000) { len = ((len & 0x7fff) << 16) | dv.getUint16(q, true); q += 2; }
      let s = '';
      for (let k = 0; k < len; k++) s += String.fromCharCode(dv.getUint16(q + k * 2, true));
      out.push(s);
    }
  }
  return out;
}

// ---- Android binary XML ----
const RES_XML_RESOURCE_MAP_TYPE = 0x0180;
const RES_XML_START_ELEMENT_TYPE = 0x0102;

const ATTR_LABEL = 0x01010001;
const ATTR_VERSION_CODE = 0x0101021b;
const ATTR_VERSION_NAME = 0x0101021c;
const ATTR_MIN_SDK = 0x0101020c;
const ATTR_TARGET_SDK = 0x01010270;

const TYPE_REFERENCE = 0x01;
const TYPE_STRING = 0x03;
const TYPE_DYNAMIC_REFERENCE = 0x07;
const TYPE_INT_DEC = 0x10;
const TYPE_INT_HEX = 0x11;

interface Attr { name: string; id: number | null; str: string | null; int: number | null; ref: number | null }

function parseAxml(bytes: Uint8Array): ParsedManifest {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (dv.getUint16(0, true) !== 0x0003) throw new Error('AndroidManifest.xml is not binary XML');
  let strings: string[] = [];
  let resMap: number[] = [];
  let found: ParsedManifest | null = null;
  let p = 8;
  while (p + 8 <= dv.byteLength) {
    const type = dv.getUint16(p, true);
    const size = dv.getUint32(p + 4, true);
    if (size < 8) throw new Error('Corrupt AXML chunk');
    if (type === RES_STRING_POOL_TYPE) {
      strings = readStringPool(dv, p);
    } else if (type === RES_XML_RESOURCE_MAP_TYPE) {
      resMap = [];
      for (let q = p + dv.getUint16(p + 2, true); q + 4 <= p + size; q += 4) resMap.push(dv.getUint32(q, true));
    } else if (type === RES_XML_START_ELEMENT_TYPE) {
      const tag = strings[dv.getUint32(p + 20, true)] ?? '';
      const attrStart = dv.getUint16(p + 24, true);
      const attrSize = dv.getUint16(p + 26, true);
      const attrCount = dv.getUint16(p + 28, true);
      const attrs: Attr[] = [];
      for (let i = 0; i < attrCount; i++) {
        const a = p + 16 + attrStart + i * attrSize;
        const nameIdx = dv.getUint32(a + 4, true);
        const rawValueIdx = dv.getUint32(a + 8, true);
        const dataType = dv.getUint8(a + 15);
        const data = dv.getUint32(a + 16, true);
        attrs.push({
          name: strings[nameIdx] ?? '',
          id: nameIdx < resMap.length ? resMap[nameIdx] : null,
          str: dataType === TYPE_STRING ? (strings[rawValueIdx] ?? strings[data] ?? '') : null,
          int: dataType === TYPE_INT_DEC || dataType === TYPE_INT_HEX ? data : null,
          ref: dataType === TYPE_REFERENCE || dataType === TYPE_DYNAMIC_REFERENCE ? data : null,
        });
      }
      // By resource id first: shrunk or obfuscated manifests blank the names.
      const attr = (name: string, id?: number) => (id !== undefined ? attrs.find((x) => x.id === id) : undefined)
        ?? attrs.find((x) => x.name === name && (id === undefined || x.id === null || x.id === id));
      if (tag === 'manifest' && !found) {
        const split = attr('split')?.str ?? null;
        found = {
          packageName: attr('package')?.str ?? '', versionCode: attr('versionCode', ATTR_VERSION_CODE)?.int ?? 0,
          versionName: attr('versionName', ATTR_VERSION_NAME)?.str ?? null, minSdk: null, targetSdk: null,
          isSplit: split !== null && split !== '', splitName: split, labelText: null, labelRef: null,
        };
      } else if (tag === 'uses-sdk' && found) {
        found.minSdk = attr('minSdkVersion', ATTR_MIN_SDK)?.int ?? found.minSdk;
        found.targetSdk = attr('targetSdkVersion', ATTR_TARGET_SDK)?.int ?? found.targetSdk;
      } else if (tag === 'application' && found) {
        const label = attr('label', ATTR_LABEL);
        found.labelText = label?.str || null;
        found.labelRef = label?.ref ?? null;
        return found;     // nothing we need comes after <application>
      }
    }
    p += size;
  }
  if (found) return found;
  throw new Error('No <manifest> element found');
}

// ---- resources.arsc: resolve one string resource ----
const RES_TABLE_TYPE = 0x0002;
const RES_TABLE_PACKAGE_TYPE = 0x0200;
const RES_TABLE_TYPE_TYPE = 0x0201;
const FLAG_SPARSE = 0x01;
const FLAG_OFFSET16 = 0x02;
const ENTRY_FLAG_COMPLEX = 0x0001;
const ENTRY_FLAG_COMPACT = 0x0008;

/** The default config scores highest, then English, then anything. */
function configScore(dv: DataView, cfg: number): number {
  const l0 = dv.getUint8(cfg + 8); const l1 = dv.getUint8(cfg + 9);
  if (l0 === 0 && l1 === 0) return 2;
  if (l0 === 0x65 && l1 === 0x6e) return 1; // "en"
  return 0;
}

/** The value of entry `entry` in the RES_TABLE_TYPE chunk at `p`, or null when it has none. */
function entryValue(dv: DataView, p: number, entry: number): { type: number; data: number } | null {
  const headerSize = dv.getUint16(p + 2, true);
  const flags = dv.getUint8(p + 9);
  const entryCount = dv.getUint32(p + 12, true);
  const entriesStart = dv.getUint32(p + 16, true);
  const offs = p + headerSize;
  let offset: number | null = null;
  if (flags & FLAG_SPARSE) {
    for (let i = 0; i < entryCount; i++) {
      if (dv.getUint16(offs + i * 4, true) === entry) { offset = dv.getUint16(offs + i * 4 + 2, true) * 4; break; }
    }
  } else if (entry < entryCount) {
    if (flags & FLAG_OFFSET16) { const o = dv.getUint16(offs + entry * 2, true); offset = o === 0xffff ? null : o * 4; }
    else { const o = dv.getUint32(offs + entry * 4, true); offset = o === 0xffffffff ? null : o; }
  }
  if (offset === null) return null;
  const e = p + entriesStart + offset;
  const eFlags = dv.getUint16(e + 2, true);
  if (eFlags & ENTRY_FLAG_COMPACT) return { type: eFlags >> 8, data: dv.getUint32(e + 4, true) };
  if (eFlags & ENTRY_FLAG_COMPLEX) return null;
  const v = e + dv.getUint16(e, true);
  return { type: dv.getUint8(v + 3), data: dv.getUint32(v + 4, true) };
}

/** The string a resource id resolves to (following references), or null. */
export function resolveStringResource(table: Uint8Array, resId: number, depth = 0): string | null {
  if (depth > 5) return null;
  const dv = new DataView(table.buffer, table.byteOffset, table.byteLength);
  if (dv.byteLength < 12 || dv.getUint16(0, true) !== RES_TABLE_TYPE) return null;
  const pkgId = (resId >>> 24) & 0xff;
  const typeId = (resId >>> 16) & 0xff;
  const entry = resId & 0xffff;
  let globalStrings: string[] = [];
  const packages: Array<{ id: number; p: number; size: number }> = [];
  for (let p = dv.getUint16(2, true); p + 8 <= dv.byteLength;) {
    const type = dv.getUint16(p, true);
    const size = dv.getUint32(p + 4, true);
    if (size < 8) return null;
    if (type === RES_STRING_POOL_TYPE) globalStrings = readStringPool(dv, p);
    else if (type === RES_TABLE_PACKAGE_TYPE) packages.push({ id: dv.getUint32(p + 8, true), p, size });
    p += size;
  }
  // Shared libraries (WebView among them) reference their own resources as package 0.
  const pkg = packages.find((x) => x.id === pkgId) ?? (packages.length === 1 || pkgId === 0 ? packages[0] : undefined);
  if (!pkg) return null;
  let best: { type: number; data: number } | null = null;
  let bestScore = -1;
  for (let p = pkg.p + dv.getUint16(pkg.p + 2, true); p + 8 <= pkg.p + pkg.size;) {
    const type = dv.getUint16(p, true);
    const size = dv.getUint32(p + 4, true);
    if (size < 8) return null;
    if (type === RES_TABLE_TYPE_TYPE && dv.getUint8(p + 8) === typeId) {
      const score = configScore(dv, p + 20);
      if (score > bestScore) {
        const v = entryValue(dv, p, entry);
        if (v) { best = v; bestScore = score; }
      }
    }
    p += size;
  }
  if (!best) return null;
  if (best.type === TYPE_STRING) return globalStrings[best.data] ?? null;
  if (best.type === TYPE_REFERENCE || best.type === TYPE_DYNAMIC_REFERENCE) return resolveStringResource(table, best.data, depth + 1);
  return null;
}

// ---- label guess ----
const GENERIC_SEGMENTS = new Set(['com', 'org', 'net', 'co', 'android', 'app', 'apps', 'mobile', 'client', 'main']);

/** A readable name from a package: the last meaningful segment, words split and capitalised. */
export function guessLabelFromPackage(pkg: string): string {
  const segs = pkg.split('.').filter(Boolean);
  const seg = [...segs].reverse().find((s) => !GENERIC_SEGMENTS.has(s.toLowerCase())) ?? segs[segs.length - 1] ?? pkg;
  return seg
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

// ---- DER: just enough to walk a certificate ----
interface Tlv { tag: number; off: number; start: number; end: number }

function tlv(b: Uint8Array, off: number): Tlv {
  if (off + 2 > b.length) throw new Error('Truncated DER');
  const tag = b[off];
  let len = b[off + 1];
  let start = off + 2;
  if (len & 0x80) {
    const n = len & 0x7f;
    if (n === 0 || n > 4) throw new Error('Unsupported DER length');
    len = 0;
    for (let i = 0; i < n; i++) len = len * 256 + b[start + i];
    start += n;
  }
  const end = start + len;
  if (end > b.length) throw new Error('Truncated DER');
  return { tag, off, start, end };
}

function children(b: Uint8Array, t: Tlv): Tlv[] {
  const out: Tlv[] = [];
  for (let p = t.start; p < t.end;) { const c = tlv(b, p); out.push(c); p = c.end; }
  return out;
}

function derString(b: Uint8Array, t: Tlv): string {
  const bytes = b.subarray(t.start, t.end);
  if (t.tag === 0x1e) { // BMPString, UTF-16BE
    let s = '';
    for (let i = 0; i + 1 < bytes.length; i += 2) s += String.fromCharCode((bytes[i] << 8) | bytes[i + 1]);
    return s;
  }
  if (t.tag === 0x14) return Array.from(bytes, (c) => String.fromCharCode(c)).join(''); // T61, Latin-1 in practice
  return new TextDecoder().decode(bytes);
}

const OID_CN = '550403';
const OID_O = '55040a';
const hex = (b: Uint8Array) => Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');

/** O= of the certificate's subject, else CN=, skipping keytool's placeholder "Unknown". */
export function vendorFromCertificate(cert: Uint8Array): string | null {
  try {
    const tbs = children(cert, tlv(cert, 0))[0];
    const parts = children(cert, tbs);
    const i = parts[0]?.tag === 0xa0 ? 1 : 0;           // optional [0] version
    const subject = parts[i + 4];                      // serial, sigAlg, issuer, validity, subject
    if (!subject || subject.tag !== 0x30) return null;
    const found: Record<string, string> = {};
    for (const rdn of children(cert, subject)) {
      for (const atv of children(cert, rdn)) {
        const [o, v] = children(cert, atv);
        if (!o || !v) continue;
        const key = hex(cert.subarray(o.start, o.end));
        const val = derString(cert, v).trim();
        if (!found[key] && val && val.toLowerCase() !== 'unknown') found[key] = val;
      }
    }
    return found[OID_O] ?? found[OID_CN] ?? null;
  } catch {
    return null;
  }
}

/** The certificates in a PKCS#7 SignedData ContentInfo (META-INF/*.RSA and friends). */
function certificatesFromPkcs7(b: Uint8Array): Uint8Array[] {
  const content = children(b, tlv(b, 0))[1];
  if (!content || content.tag !== 0xa0) return [];
  const signedData = children(b, content)[0];
  const certs = signedData ? children(b, signedData).find((c) => c.tag === 0xa0) : undefined;
  return certs ? children(b, certs).map((c) => b.subarray(c.off, c.end)) : [];
}

// ---- APK Signing Block (v2/v3), just before the central directory ----
const SCHEMES: Array<{ id: number; source: 'v2' | 'v3' }> = [
  { id: 0x1b93ad61, source: 'v3' }, // v3.1
  { id: 0xf05368c0, source: 'v3' },
  { id: 0x7109871a, source: 'v2' },
];

async function signingBlockCert(file: Blob, cenOffset: number): Promise<{ cert: Uint8Array; source: 'v2' | 'v3' } | null> {
  if (cenOffset < 32) return null;
  const foot = await readRange(file, cenOffset - 24, cenOffset);
  if (new TextDecoder().decode(new Uint8Array(foot.buffer, foot.byteOffset + 8, 16)) !== 'APK Sig Block 42') return null;
  const size = foot.getUint32(0, true) + foot.getUint32(4, true) * 2 ** 32;
  if (size > 16 * 1024 * 1024 || size + 8 > cenOffset) return null;
  const block = await readRange(file, cenOffset - size - 8, cenOffset - 24);
  const pairs = new Map<number, { start: number; end: number }>();
  for (let p = 8; p + 12 <= block.byteLength;) {
    const len = block.getUint32(p, true) + block.getUint32(p + 4, true) * 2 ** 32;
    if (len < 4 || p + 8 + len > block.byteLength) break;
    pairs.set(block.getUint32(p + 8, true), { start: p + 12, end: p + 8 + len });
    p += 8 + len;
  }
  for (const s of SCHEMES) {
    const v = pairs.get(s.id);
    if (!v) continue;
    try {
      // signers (len) → first signer (len) → its signed data (len) → digests (len, skipped)
      // → certificates (len) → first certificate (len, DER)
      let q = v.start + 12;
      q += 4 + block.getUint32(q, true);
      q += 4;
      const certLen = block.getUint32(q, true); q += 4;
      if (q + certLen > v.end) continue;
      return { cert: new Uint8Array(block.buffer, block.byteOffset + q, certLen), source: s.source };
    } catch { /* malformed: try the next scheme */ }
  }
  return null;
}

// ---- public ----
const manifestOnly = (m: ParsedManifest): ApkManifestInfo => ({
  packageName: m.packageName, versionCode: m.versionCode, versionName: m.versionName,
  minSdk: m.minSdk, targetSdk: m.targetSdk, isSplit: m.isSplit, splitName: m.splitName,
});

export async function readApkManifest(file: Blob): Promise<ApkManifestInfo> {
  return manifestOnly(parseAxml(await zipEntry(file, await zipIndex(file), 'AndroidManifest.xml')));
}

/** Everything the file can tell the App Library: the manifest, the app's label and the vendor. */
export async function readApkInfo(file: Blob): Promise<ApkInfo> {
  const index = await zipIndex(file);
  const m = parseAxml(await zipEntry(file, index, 'AndroidManifest.xml'));
  if (m.isSplit) return { ...manifestOnly(m), label: null, labelGuessed: false, vendor: null, vendorSource: null };

  let label = m.labelText;
  if (!label && m.labelRef !== null && index.entries.some((e) => e.name === 'resources.arsc')) {
    label = await zipEntry(file, index, 'resources.arsc').then((t) => resolveStringResource(t, m.labelRef!)).catch(() => null);
  }
  const labelGuessed = !label;
  if (!label) label = m.packageName ? guessLabelFromPackage(m.packageName) : null;

  let vendor: string | null = null;
  let vendorSource: ApkInfo['vendorSource'] = null;
  const v1 = index.entries.find((e) => /^META-INF\/[^/]+\.(RSA|DSA|EC)$/i.test(e.name));
  if (v1) {
    const cert = await readEntry(file, v1).then((b) => certificatesFromPkcs7(b)[0] ?? null).catch(() => null);
    vendor = cert ? vendorFromCertificate(cert) : null;
    if (vendor) vendorSource = 'v1';
  }
  if (!vendor) {
    const s = await signingBlockCert(file, index.cenOffset).catch(() => null);
    vendor = s ? vendorFromCertificate(s.cert) : null;
    if (s && vendor) vendorSource = s.source;
  }
  return { ...manifestOnly(m), label, labelGuessed, vendor, vendorSource };
}

/** sha256 of a whole file, lower-case hex. Reads the file once. */
export async function sha256File(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}
